import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ReplyBox } from "./ReplyBox";

interface Message {
  id: string;
  content: string;
  text?: { body: string };
  message?: string;
  type?: string;
  from?: string;
  direction: "inbound" | "outbound";
  timestamp: Date | null;
  status?: string;
}

interface MessageThreadProps {
  userId: string;
  userName: string;
  userPhone: string;
  messages: Message[];
  onRefresh: () => void;
}

const getMessageText = (msg: Message): string => {
  // Check for image/media messages
  if (msg.type === 'image' || msg.type === 'video' || msg.type === 'audio' || msg.type === 'document') {
    return `[${msg.type.toUpperCase()}]`;
  }
  
  // Check for button/interactive messages
  if (msg.type === 'button' || msg.type === 'interactive') {
    return '[INTERACTIVE]';
  }
  
  // Check for text content - priority order
  if (msg.content && String(msg.content).trim()) return String(msg.content);
  if (msg.text?.body && String(msg.text.body).trim()) return String(msg.text.body);
  if (msg.message && String(msg.message).trim()) return String(msg.message);
  
  // Handle context body for interactive messages
  if ((msg as any).context?.body) return String((msg as any).context.body);
  
  // Fallback for unknown message types with logging
  if (msg.type) {
    console.warn(`Unsupported message type: ${msg.type}`, msg);
    return `[${msg.type}]`;
  }
  
  return '[No content]';
};

const formatTime = (timestamp: Date | null): string => {
  if (!timestamp) return "";
  try {
    return timestamp.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

export const MessageThread: React.FC<MessageThreadProps> = ({
  userId,
  userName,
  userPhone,
  messages,
  onRefresh,
}) => {
  const [isSending, setIsSending] = useState(false);

  const functions = getFunctions(undefined, "asia-south1");
  const sendAdminReply = httpsCallable(functions, "sendAdminReply");
  const sendButtonMessage = httpsCallable(functions, "sendButtonMessage");

  const handleQuickReply = async (message: string) => {
    // just prefill + send via ReplyBox logic
    if (!message.trim() || isSending) return;
    try {
      setIsSending(true);
      await sendAdminReply({
        phoneNumber: userPhone,
        message,
        userId,
      });
      setTimeout(() => onRefresh(), 500);
    } catch (error) {
      console.error("Error sending quick reply:", error);
      alert("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleSendButtons = async (
    bodyText: string,
    buttons: Array<{ id: string; title: string }>
  ) => {
    try {
      setIsSending(true);
      await sendButtonMessage({
        phoneNumber: userPhone,
        bodyText,
        buttons,
        userId,
      });
      setTimeout(() => onRefresh(), 500);
    } catch (error) {
      console.error("Error sending buttons:", error);
      alert("Failed to send button message");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid #ddd",
          backgroundColor: "#075E54",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>{userName || userId}</h3>
          <p
            style={{
              fontSize: "14px",
              opacity: 0.9,
              margin: "4px 0 0",
            }}
          >
            {messages.length} messages
          </p>
        </div>
        <button
          onClick={onRefresh}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "bold",
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px",
          backgroundColor: "#ECE5DD",
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              marginTop: "40px",
              fontSize: "16px",
            }}
          >
            No messages in this conversation yet
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: "flex",
                justifyContent:
                  msg.direction === "outbound" ? "flex-end" : "flex-start",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  maxWidth: "65%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor:
                    msg.direction === "outbound" ? "#DCF8C6" : "white",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    wordWrap: "break-word",
                  }}
                >
                  {getMessageText(msg)}
                </p>

                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "11px",
                    color: "#667",
                    textAlign: "right",
                  }}
                >
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer: Quick Replies, Interactive Buttons, ReplyBox */}
      <div
        style={{
          padding: "16px",
          borderTop: "1px solid #ddd",
          backgroundColor: "#F0F0F0",
          flexShrink: 0,
        }}
      >
        {/* Quick Reply Buttons */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          <button
            onClick={() =>
              handleQuickReply(
                "Welcome to Ruchira! 🍽️ Browse our delicious home-cooked meals."
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#E3F2FD",
              color: "#1976D2",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            👋 Welcome
          </button>

          <button
            onClick={() =>
              handleQuickReply(
                "You can browse our menu and place orders through our app."
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#F3E5F5",
              color: "#7B1FA2",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            📱 How to Order
          </button>

          <button
            onClick={() =>
              handleQuickReply(
                "Your order has been confirmed! ✅ Delivery time: 30-45 minutes."
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#E8F5E9",
              color: "#388E3C",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            ✅ Order Confirmed
          </button>

          <button
            onClick={() =>
              handleQuickReply(
                "Your order is out for delivery! 🚚 Track your delivery agent in real-time."
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#FFF3E0",
              color: "#F57C00",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            🚚 Out for Delivery
          </button>

          <button
            onClick={() =>
              handleQuickReply(
                "Thank you for choosing Ruchira! 😊 We hope you enjoyed your meal!"
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#FCE4EC",
              color: "#C2185B",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            🙏 Thank You
          </button>

          <button
            onClick={() =>
              handleQuickReply(
                "We're here to help! What issue are you facing?"
              )
            }
            disabled={isSending}
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              backgroundColor: "#E0F2F1",
              color: "#00796B",
              border: "none",
              borderRadius: "16px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.5 : 1,
            }}
          >
            🆘 Support
          </button>
        </div>

        {/* Interactive Buttons */}
        <div
          style={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px solid #ddd",
          }}
        >
          <p
            style={{
              fontSize: "12px",
              color: "#666",
              marginBottom: "8px",
              marginTop: 0,
            }}
          >
            Send Interactive Buttons:
          </p>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              onClick={() =>
                handleSendButtons("How can we help you today?", [
                  { id: "menu", title: "📋 View Menu" },
                  { id: "order", title: "🛒 Place Order" },
                  { id: "support", title: "💬 Support" },
                ])
              }
              disabled={isSending}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                backgroundColor: "#D1C4E9",
                color: "#5E35B1",
                border: "1px solid #5E35B1",
                borderRadius: "16px",
                cursor: isSending ? "not-allowed" : "pointer",
                opacity: isSending ? 0.5 : 1,
              }}
            >
              🎯 Main Menu
            </button>

            <button
              onClick={() =>
                handleSendButtons("Rate your recent order:", [
                  { id: "rate_5", title: "⭐⭐⭐⭐⭐" },
                  { id: "rate_4", title: "⭐⭐⭐⭐" },
                  { id: "rate_3", title: "⭐⭐⭐" },
                ])
              }
              disabled={isSending}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                backgroundColor: "#FFF9C4",
                color: "#F57F17",
                border: "1px solid #F57F17",
                borderRadius: "16px",
                cursor: isSending ? "not-allowed" : "pointer",
                opacity: isSending ? 0.5 : 1,
              }}
            >
              ⭐ Rating
            </button>

            <button
              onClick={() =>
                handleSendButtons("Do you want to confirm this order?", [
                  { id: "confirm_yes", title: "✅ Yes, Confirm" },
                  { id: "confirm_no", title: "❌ Cancel" },
                ])
              }
              disabled={isSending}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                backgroundColor: "#C8E6C9",
                color: "#2E7D32",
                border: "1px solid #2E7D32",
                borderRadius: "16px",
                cursor: isSending ? "not-allowed" : "pointer",
                opacity: isSending ? 0.5 : 1,
              }}
            >
              ✅ Confirm/Cancel
            </button>
          </div>
        </div>

        {/* Main Reply Box */}
        <ReplyBox
          onSend={async (text) => {
            if (!text.trim() || isSending) return;
            try {
              setIsSending(true);
              await sendAdminReply({
                phoneNumber: userPhone,
                message: text,
                userId,
              });
              setTimeout(() => onRefresh(), 500);
            } catch (error) {
              console.error("Error sending reply:", error);
              alert("Failed to send message");
            } finally {
              setIsSending(false);
            }
          }}
        />
      </div>
    </div>
  );
};
